import { gql } from "generated-graphql";

/**
 * Shared row shape for the areas list. `volume` carries both the satellite
 * gauge (nonAcute vs. the 55-gal SAA threshold) and the acute component,
 * which only renders when the area holds acute waste.
 */
gql(`
  fragment AccumulationAreaListItem on WasteAccumulationArea {
    id
    name
    type
    description
    latitude
    longitude
    containerCount
    oldestMovedToCentralDate
    nearestDeadlineDate
    archivedAt
    volume {
      status
      exempt
      moveDeadline
      nonAcute {
        total
        unit
        threshold
        thresholdUnit
        status
      }
      acute {
        totalVolume
        volumeUnit
        volumeThreshold
        volumeThresholdUnit
        totalKg
        kgThreshold
        status
      }
    }
  }
`);

/** Facility-scoped areas list — this page has no tenant-wide mode. */
export const FACILITY_ACCUMULATION_AREAS_QUERY = gql(`
  query FacilityAccumulationAreas($facilityId: ID!, $search: String, $sort: [SortModel!]) {
    facility(id: $facilityId) {
      id
      name
      epaIdStatuses {
        epaId
        isPrimaryForFacility
      }
      accumulationAreas(search: $search, sort: $sort) {
        items {
          ...AccumulationAreaListItem
        }
        count
      }
    }
  }
`);

/**
 * Lightweight variant for the facility dashboard tile, which needs only the
 * area count and how many are over threshold — not the full list-item shape.
 * Selecting only `volume { status }` avoids triggering the per-area container
 * aggregate resolvers (containerCount / oldest / nearest) the list needs.
 */
export const FACILITY_ACCUMULATION_AREAS_TILE_QUERY = gql(`
  query FacilityAccumulationAreasTile($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      accumulationAreas {
        items {
          id
          volume {
            status
          }
        }
        count
      }
    }
  }
`);

export const GET_ACCUMULATION_AREA_QUERY = gql(`
  query GetAccumulationArea($id: ID!) {
    accumulationArea(id: $id) {
      ...AccumulationAreaListItem
      facilityId
    }
  }
`);

export const VALIDATE_ACCUMULATION_AREA_INPUT = gql(`
  query ValidateAccumulationAreaInput($input: CreateAccumulationAreaInput!, $currentAreaId: ID) {
    accumulationAreaInputValidator(input: $input, currentAreaId: $currentAreaId) {
      ...issue
    }
  }
`);

export const CREATE_ACCUMULATION_AREA_MUTATION = gql(`
  mutation CreateAccumulationArea($input: CreateAccumulationAreaInput!) {
    createAccumulationArea(input: $input) {
      ...AccumulationAreaListItem
    }
  }
`);

export const UPDATE_ACCUMULATION_AREA_MUTATION = gql(`
  mutation UpdateAccumulationArea($input: UpdateAccumulationAreaInput!) {
    updateAccumulationArea(input: $input)
  }
`);

export const DELETE_ACCUMULATION_AREA_MUTATION = gql(`
  mutation DeleteAccumulationArea($id: ID!) {
    deleteAccumulationArea(id: $id) {
      id
      archived
    }
  }
`);

export const MOVE_CONTAINER_MUTATION = gql(`
  mutation MoveContainer($input: MoveContainerInput!) {
    moveContainer(input: $input)
  }
`);

/**
 * The standing ruleset behind an area type's clock/caps, for the "why this
 * limit" popover. Pins the episodic axis off (no `hasActiveEpisodicEvent`
 * input) — this surface explains the facility's default rules, not what
 * applies during an active episodic event.
 */
export const FACILITY_ACCUMULATION_RULESET_QUERY = gql(`
  query FacilityAccumulationRuleset($facilityId: ID!, $areaType: WasteAccumulationAreaType!) {
    facility(id: $facilityId) {
      id
      accumulationRuleset(areaType: $areaType) {
        areaType
        generatorStatusResolved
        clock { limitType jurisdiction citation days }
        caps { limitType jurisdiction citation nonAcuteGal acuteGal acuteKg moveWindowDays }
      }
    }
  }
`);
